import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { saveUassistSummary } from "@/core/app/slices/uassist/formThunkApi"
import { RootState } from "@/core/app/store"
import JsonFormBuilder from "@/rx-form/builder/JsonFormBuilder"
import {
  generateSummaryData,
  generateSummarySchema,
  generateSummaryUiSchema,
} from "@/rx-form/components/NewForm/formUtils"
import { UText } from "@/ui-component"
import { Box, Grid } from "@mui/material"
import {
  Document,
  Image,
  Page,
  pdf,
  StyleSheet,
  View,
} from "@react-pdf/renderer"
import html2canvas from "html2canvas"
import { PDFDocument } from "pdf-lib"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import { Trans } from "react-i18next"
import { useParams } from "react-router-dom"
import { useFormContext } from "./FormContext"
import { displayMetaType } from "./types"
import { downloadDentalChart } from "@/core/app/slices/records/xrays"

const A4Height = 792 // 792px is the height of a standard letter-sized page in points (11 inches).
const A4Margin = 50

const styles = StyleSheet.create({
  page: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    opacity: 1,
    paddingTop: `${A4Margin}px`,
    paddingBottom: `${A4Margin}px`,
  },
})

/**
 * Adds pages from one PDF document to another.
 * @param mergedPdfDoc The PDF document to add pages to.
 * @param pdfDoc The source PDF document.
 * @param pageIndices The indices of pages to add.
 */
const addPagesToMergedPDF = async (
  mergedPdfDoc: PDFDocument,
  pdfDoc: PDFDocument,
  pageIndices: number[],
) => {
  for (const index of pageIndices) {
    const [pdfPage] = await mergedPdfDoc.copyPages(pdfDoc, [index])
    mergedPdfDoc.addPage(pdfPage)
  }
}

/**
 * Ref interface for imperative methods exposed by PdfContent.
 */
export interface PdfContentRef {
  contentToPDF: React.RefObject<HTMLDivElement>
  exportComponentAsPDF: (
    contentToPDF: React.RefObject<HTMLDivElement>,
  ) => Promise<object | undefined>
}

interface PdfContentProps {
  displayMeta?: displayMetaType
}

/**
 * Component for rendering and exporting a PDF summary of the form.
 * Exposes an imperative ref for PDF export.
 */
export const PdfContent = forwardRef<PdfContentRef, PdfContentProps>((props, ref) => {
  const { formName, data, schema, uiSchema, renderedTemplate } =
    useFormContext()
  const { displayMeta } = props
  const contentToPDF = useRef<HTMLDivElement>(null)

  const isRefinement = [
    FORM_CONSTANT.REFINEMENT,
    FORM_CONSTANT.REFINEMENT_2,
  ].includes(formName)
  const isRetainer = formName === FORM_CONSTANT.RETAINER
  const { dentalChart } = useAppSelector(
    (state: RootState) => state.xrayService,
  )

  const { patientData } = useAppSelector(
    (state: RootState) => state.patientService,
  )

  const dispatch = useAppDispatch()
  const { caseId, patientId } = useParams<{ caseId: string; patientId: string }>()
  const patientDetail = [
    {
      label: "Patient Name",
      value: patientData?.firstName + " " + patientData?.lastName,
    },
    {
      label: "Patient Id",
      value: patientId || patientData.id,
    },
    {
      label: "Case Id",
      value: caseId,
    },
  ]

  useEffect(() => {
    if (formName === FORM_CONSTANT.PRESCRIPTION && contentToPDF?.current) {
      dispatch(downloadDentalChart({ patientId, caseId }))
    }
  }, [contentToPDF])

  /**
   * Exports the referenced component as a PDF, merging with dental chart if present.
   * @param contentToPDF The ref to the HTML content to export.
   * @returns A promise resolving to the PDF upload response or undefined.
   */
  const exportComponentAsPDF = async (
    contentToPDF: React.RefObject<HTMLDivElement>,
  ): Promise<object | undefined> => {
    if (!contentToPDF.current) {
      console.warn("the component which is exported to PDF is null !")
      return undefined
    }
    contentToPDF.current.style.display = "block"
    const isRefinement = [
      FORM_CONSTANT.REFINEMENT,
      FORM_CONSTANT.REFINEMENT_2,
    ].includes(formName)
    const isRetainer = formName === FORM_CONSTANT.RETAINER
    // Now you have the Blob object, and you can use it as needed.
    const width = contentToPDF.current?.clientWidth
    // const height = contentToPDF.current?.clientHeight

    // calculate page height range without cutting-out elements in last
    let parent = contentToPDF.current
    let pageDimensionArr: { start: number; end: number; lastChildEl: Element | null }[] = []
    let currentPage = 0
    const pageH = A4Height - 2 * A4Margin
    let lastScannedH = 0
    let parentDimension = parent.getBoundingClientRect()

    parent.querySelectorAll(".summary-data-row").forEach((el, index) => {
      if (!pageDimensionArr[currentPage]) {
        pageDimensionArr[currentPage] = {
          start: lastScannedH,
          end: lastScannedH + pageH,
          lastChildEl: null,
        }
      }
      let elDimension = el.getBoundingClientRect()
      let elRelTop = Math.abs(parentDimension.top - elDimension.top)
      let elRelBottom = elRelTop + elDimension.height

      if (pageDimensionArr[currentPage].end > elRelBottom) {
        // element is within render range
        lastScannedH = elRelBottom
        pageDimensionArr[currentPage].lastChildEl = el
      } else {
        // element is NOT within render range
        lastScannedH = elRelTop
        pageDimensionArr[currentPage].end = elRelTop
        pageDimensionArr[currentPage + 1] = {
          start: elRelTop,
          end: elRelTop + pageH,
          lastChildEl: el,
        }
        currentPage += 1
      }
    })

    const imgPromises: Promise<string>[] = []

    for (let i = 0; i < pageDimensionArr.length; i++) {
      const imgPromise = html2canvas(contentToPDF.current, {
        y: pageDimensionArr[i].start,
        height: pageDimensionArr[i].end - pageDimensionArr[i].start,
      }).then((canvas) => {
        return canvas.toDataURL("image/png")
      })

      imgPromises.push(imgPromise)
    }

    return Promise.all(imgPromises).then((imgDataArray) => {
      const MakePDFComponent = () => (
        <Document>
          {imgDataArray.map((imgData, i) => {
            return (
              <Page key={i} style={styles.page} size="LETTER">
                <View>
                  <Image
                    style={{ width, height: `${pageH}px` }}
                    source={imgData}
                    cache={true}
                  />
                </View>
              </Page>
            )
          })}
        </Document>
      )

      return pdf(<MakePDFComponent />)
        .toBlob()
        .then(async (blob) => {
          if (isRefinement) {
            return placeUassistOrderRefinement(blob, isRefinement)
          } else {
            const pdfDoc1 = await PDFDocument.load(await blob.arrayBuffer())
            const mergedPdfDoc = await PDFDocument.create()

            const pageIndices1 = Array.from(
              { length: pdfDoc1.getPageCount() },
              (_, i) => i,
            )

            await addPagesToMergedPDF(mergedPdfDoc, pdfDoc1, pageIndices1)
            if (formName === FORM_CONSTANT.PRESCRIPTION && dentalChart) {
              const pdfDoc2 = await PDFDocument.load(
                await dentalChart.arrayBuffer(),
              )
              const pageIndices2 = Array.from(
                { length: pdfDoc2.getPageCount() },
                (_, i) => i,
              )
              await addPagesToMergedPDF(mergedPdfDoc, pdfDoc2, pageIndices2)
            }
            const mergedPdfBlob = new Blob([await mergedPdfDoc.save()], {
              type: "application/pdf",
            })

            // const link = document.createElement("a")
            // link.href = URL.createObjectURL(mergedPdfBlob)
            // link.download = "summary.pdf"
            // link.click()

            let pdfName = isRetainer ? "retainer-summary.pdf" : "summary.pdf"
            const formData = new FormData()
            formData.append(
              "attachment",
              new File([mergedPdfBlob], pdfName, {
                type: "application/pdf",
              }),
            )

            return dispatch(
              saveUassistSummary({
                patientId: patientId || patientData.id,
                caseId: caseId,
                payload: formData,
                isRefinement,
                isRetainer,
              }),
            )
          }
        })
    })
  }

  /**
   * Uploads the refinement PDF to the server.
   * @param blob The PDF blob.
   * @param isRefinement Whether this is a refinement.
   * @returns The upload response.
   */
  const placeUassistOrderRefinement = (blob: Blob, isRefinement: boolean) => {
    const formData = new FormData()
    formData.append(
      "attachment",
      new File([blob], `refinement.pdf`, {
        type: "application/pdf",
      }),
    )
    formData.append("is_refinement", "1")
    return dispatch(
      saveUassistSummary({
        patientId: patientId || patientData.id,
        caseId: caseId,
        payload: formData,
        isRefinement,
      }),
    )
  }
  useImperativeHandle(ref, () => ({
    contentToPDF,
    exportComponentAsPDF,
  }))

  return (
    <>
      <Grid>
        <Box
          component={"div"}
          ref={contentToPDF}
          sx={{
            zIndex: -1,
            position: "fixed",
            top: "120vh",
            display: "none",
            width: "532px",
          }}
        >
          <Box component={"div"}>
            <UText
              variant={"h4"}
              component={"div"}
              sxProp={{ textAlign: "center" }}
            >
              <Trans components={{ newLine: <br /> }}>
                {displayMeta?.formTitle}
              </Trans>
            </UText>
          </Box>
          {patientDetail.map((data) => {
            return (
              <Box
                sx={{
                  py: 1,
                  mb: "4px",
                  display: "flex",
                  flexDirection: "row",
                  pl: 1,
                }}
                key={data.label}
              >
                <UText
                  variant={"body2"}
                  sxProp={{
                    width: 200,
                    color: "rgba(0, 0, 0, 0.60)",
                  }}
                >
                  {data.label}
                </UText>
                <Box>
                  <UText
                    component={"div"}
                    variant={"subtitle2"}
                    sxProp={{
                      ml: 1,
                      lineHeight: "16px",
                      color: "text.primary",
                    }}
                  >
                    {data.value}
                  </UText>
                </Box>
              </Box>
            )
          })}
          <JsonFormBuilder
            schema={generateSummarySchema(
              schema,
              renderedTemplate?.schema || {},
            )}
            uischema={generateSummaryUiSchema(
              uiSchema,
              data,
              renderedTemplate?.uiSchema || {},
              true,
            )}
            defaultData={generateSummaryData({
              ...data,
              ...(renderedTemplate?.data || {}),
            })}
            isFormSubmitted={false}
            setIsFormSubmitted={() => {}}
          />
        </Box>
      </Grid>
    </>
  )
})
